"use client"

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { petStore, type Pet } from '@/lib/petStore'



interface SimplePetContextType {
  currentPet: Pet | null
  setCurrentPet: (pet: Pet | null) => void
  clearCurrentPet: () => void
  isLoading: boolean
  linkPetById: (petId: number) => Promise<void>
  linkPetByUuid: (petId: string) => Promise<void>
}

const SimplePetContext = createContext<SimplePetContextType | undefined>(undefined)

export function SimplePetProvider({ children }: { children: ReactNode }) {
  const [currentPet, setCurrentPetState] = useState<Pet | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Subscribe to pet store changes
  useEffect(() => {
    const unsubscribe = petStore.subscribe((pet) => {
      setCurrentPetState(pet)
    })
    
    return unsubscribe
  }, [])

  // Fetch pet data by ID (authenticated)
  const fetchPetById = async (petId: number): Promise<Pet | null> => {
    try {
      const response = await fetch(`/api/pets/${petId}`)
      if (!response.ok) {
        throw new Error('Pet not found')
      }
      const data = await response.json()
      return data.pet
    } catch (error) {
      console.error('Error fetching pet:', error)
      return null
    }
  }

  // Fetch pet data by UUID (public track endpoint).
  //
  // A genuine 404 (pet really doesn't exist) returns null so the caller can
  // show the access wall. Everything else — 5xx, network error, aborted
  // fetch — is treated as transient and retried with backoff. On a serverless
  // boundary the first request after idle can hit a cold DB connection and
  // return a 500; the old code flattened that into "Pet not found", which both
  // misled debugging and spammed Rollbar. Retrying absorbs the cold start;
  // if it still fails we throw the real error so it's logged once, accurately.
  const fetchPetByUuid = async (petId: string): Promise<Pet | null> => {
    const MAX_ATTEMPTS = 3
    let lastError: unknown
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        const response = await fetch(`/api/pets/${petId}/track`)
        if (response.ok) {
          const data = await response.json()
          return data.pet
        }
        // The pet genuinely isn't there — don't retry.
        if (response.status === 404) {
          return null
        }
        // 5xx / unexpected status — transient, fall through to retry.
        lastError = new Error(`Track request failed with status ${response.status}`)
      } catch (error) {
        // Network failure or aborted fetch — also transient.
        lastError = error
      }
      if (attempt < MAX_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, attempt * 400))
      }
    }
    throw lastError instanceof Error ? lastError : new Error('Failed to load pet')
  }

  // Link pet by ID (authenticated method)
  const linkPetById = async (petId: number) => {
    setIsLoading(true)
    try {
      const pet = await fetchPetById(petId)
      if (pet) {
        petStore.setCurrentPet(pet)
      } else {
        throw new Error('Pet not found')
      }
    } catch (error) {
      console.error('Error linking pet:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // Link pet by UUID (public method for track links).
  // Errors propagate to the caller, which logs them once — fetchPetByUuid
  // returns null only for a genuine 404, so that's the one "not found" case.
  const linkPetByUuid = async (petId: string) => {
    setIsLoading(true)
    try {
      const pet = await fetchPetByUuid(petId)
      if (pet) {
        petStore.setCurrentPet(pet)
      } else {
        throw new Error('Pet not found')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Clear current pet
  const clearCurrentPet = () => {
    petStore.clearCurrentPet()
  }

  // Set current pet through the store
  const setCurrentPet = (pet: Pet | null) => {
    petStore.setCurrentPet(pet)
  }

  return (
    <SimplePetContext.Provider value={{ 
      currentPet, 
      setCurrentPet, 
      clearCurrentPet, 
      isLoading,
      linkPetById,
      linkPetByUuid
    }}>
      {children}
    </SimplePetContext.Provider>
  )
}

export function useSimplePet() {
  const context = useContext(SimplePetContext)
  if (context === undefined) {
    throw new Error('useSimplePet must be used within a SimplePetProvider')
  }
  return context
} 